import parseArgs from "minimist";
import { toGoLangInvoke, openUrlWithBrowser, fetchWindowOptions } from "../common/windowFunctions";
class FinWindowImpl {
    get name() {
        return this.fin.windowName;
    }
    focus() {
        this.fin.focus();
    }
    hide() {
        this.fin.hide();
    }
    async getSnapshot(area, cb) {
        return this.fin.getSnapshot(area, cb);
    }
    getZoomFactor(cb) {
        this.fin.getZoomFactor(cb);
    }
    setZoomFactor(zoomFactor, cb) {
        this.fin.setZoomFactor(zoomFactor, cb);
    }
    getOptions(cb) {
        this.fin.getOptions(cb);
    }
    constructor(fin) {
        this.fin = fin;
    }
}
class WindowPolyfill {
    getCurrent() {
        return new FinWindowImpl(this.fin);
    }
    focus() {
        this.fin.focus();
    }
    hide() {
        this.fin.hide();
    }
    async getSnapshot(area, cb) {
        return this.fin.getSnapshot(area, cb);
    }
    constructor(fin) {
        this.fin = fin;
    }
}
class SystemPolyfill {
    getCommandLineArguments(cb) {
        this.fin.getCommandLineArguments(cb);
    }
    constructor(fin) {
        this.fin = fin;
    }
    async openUrlWithBrowser(url, cb) {
        await this.fin.openUrlWithBrowser(url, cb);
    }
    isTitlebarWindow() {
        return this.fin.isTitlebarWindow();
    }
    isBrowserView() {
        return this.fin.isBrowserView();
    }
}
class DesktopPolyfill {
    constructor(fin) {
        this.fin = fin;
        this.Window = new WindowPolyfill(fin);
        this.System = new SystemPolyfill(fin);
    }
}
export class Fin {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.windowName = "Window name not set yet. Please wait for FSBL 'onReady' event before calling getCurrent()";
        this.myWindow = null;
        const pw = this.asyncConstructors.addConstructor();
        gluePromise.then((g) => {
            this.glue = g;
            this.myWindow = this.glue.windows?.my() ?? null;
            if (this.myWindow) {
                this.windowName = this.myWindow.id;
            }
            else {
                this.windowName = "Not a valid window. Perhaps running in node?";
            }
            this.asyncConstructors.finishConstructor(pw);
        });
        this.desktop = new DesktopPolyfill(this);
    }
    async focus() {
        await this.asyncConstructors.ready();
        await new Promise((resolve) => setTimeout(() => {
            this.myWindow?.focus();
            resolve();
        }, 250));
    }
    async hide() {
        await this.asyncConstructors.ready();
        this.myWindow?.hide();
    }
    isBrowserView() {
        return false;
    }
    isTitlebarWindow() {
        return false;
    }
    async getOptions(cb) {
        await this.asyncConstructors.ready();
        this.loggerClient.debug("getOptions start");
        const win = this.glue.windows.my();
        if (win) {
            const options = await fetchWindowOptions(win);
            cb?.(options);
            this.loggerClient.debug("getOptions return");
            return options;
        }
        else {
            this.loggerClient.error("fin getOptions() called on non window");
            cb?.({});
            return {};
        }
    }
    async getSnapshot(area, cb) {
        await this.asyncConstructors.ready();
        this.loggerClient.debug("getSnapshot start");
        if (!this.myWindow) {
            cb?.("");
            return "";
        }
        const bounds = await this.myWindow.getBounds();
        const captureOptions = {
            size: {
                height: bounds.height,
                keepAspectRatio: true,
                width: bounds.width
            }
        };
        const base64String = await this.myWindow?.capture(captureOptions);
        this.loggerClient.debug("getSnapshot return");
        cb?.(base64String);
        return base64String;
    }
    async getCommandLineArguments(cb) {
        await this.asyncConstructors.ready();
        this.loggerClient.debug("getCommandLineArguments start");
        const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.GD.Execute", { command: "get-cli-arguments" }));
        const result = parseArgs(data?.args ?? []);
        if (err) {
            this.loggerClient.error("getCommandLineArguments", err);
        }
        cb?.(result);
        this.loggerClient.debug("getCommandLineArguments return", result);
    }
    async openUrlWithBrowser(url, cb) {
        this.loggerClient.debug("openUrlWithBrowser", "start", { url });
        await this.asyncConstructors.ready();
        const { err } = await openUrlWithBrowser(this.glue, url);
        if (err) {
            this.loggerClient.error("openUrlWithBrowser", "error", err);
        }
        cb?.(err);
        this.loggerClient.debug("openUrlWithBrowser", "return");
    }
    async getZoomFactor(cb) {
        await this.asyncConstructors.ready();
        const zoomFactor = (this.glue.windows.my()?.zoomFactor ?? 100) / 100;
        cb({
            zoomFactor,
            status: "success"
        });
    }
    static getClosestZoomFactor(zoomFactor) {
        return window.iodesktop?.settings.zoom.factors.reduce((previous, current) => {
            return (Math.abs(current - zoomFactor) <= Math.abs(previous - zoomFactor) ? current : previous);
        });
    }
    async setZoomFactor(zoomFactor, cb) {
        await this.asyncConstructors.ready();
        const iocdZoomFactor = Fin.getClosestZoomFactor(zoomFactor * 100);
        if (this.glue.windows.my()?.zoomFactor === iocdZoomFactor) {
            cb?.();
            return;
        }
        const promise = new Promise((resolve) => {
            this.glue.windows.my()?.onZoomFactorChanged(() => {
                cb?.();
                resolve();
            });
        });
        await this.glue.windows.my()?.setZoomFactor(iocdZoomFactor);
        await promise;
    }
}
